import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { IconButton, XStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { sandboxTerminalPanelStore } from './sandbox-terminal-panel.store';

/** Tab label: host title (cwd or running command, whatever the shell sets), else a number. */
export function tabLabel(title: string, index: number): string {
  return title.trim() || `Terminal ${index + 1}`;
}

/**
 * Tab strip for the local terminal dock header. Click activates, middle-click or
 * the hover X kills that tab's shell, + opens a new one. Keyboard equivalents live
 * in local-terminal-keys.ts.
 */
export const LocalTerminalTabs = observer(function LocalTerminalTabs() {
  const { tabs, activeKey } = sandboxTerminalPanelStore;
  return (
    <XStack
      className="min-w-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-hide"
      role="tablist"
      aria-label="Terminals"
    >
      {tabs.map((tab, index) => {
        const active = tab.key === activeKey;
        const label = tabLabel(tab.title, index);
        return (
          <div
            key={tab.key}
            role="tab"
            aria-selected={active}
            tabIndex={-1}
            title={label}
            className={cn(
              'group flex h-6 w-52 shrink-0 cursor-default select-none items-center justify-between gap-2 rounded-lg border pl-2.5 pr-1.5 text-xs transition-colors',
              active
                ? 'border-border-strong bg-surface text-foreground shadow-sm dark:bg-surface-active'
                : 'border-transparent text-foreground-subtle hover:bg-surface-hover hover:text-foreground'
            )}
            onMouseDown={(event) => {
              // middle-click closes, like every tabbed terminal
              if (event.button === 1) {
                event.preventDefault();
                sandboxTerminalPanelStore.requestKill(tab.key);
              } else if (event.button === 0) {
                sandboxTerminalPanelStore.activateTab(tab.key);
                sandboxTerminalPanelStore.focusLocal();
              }
            }}
          >
            {/* leading icon, like chat tabs; a run tab swaps it for its status dot */}
            {tab.command === null ? (
              <Icons.terminal className="size-3.5 shrink-0 text-foreground-muted" />
            ) : (
              // agent-run command: pulsing while it runs, still once it exits
              <span className="flex size-3.5 shrink-0 items-center justify-center">
                <span
                  aria-label={tab.exited ? 'Finished' : 'Running'}
                  className={cn(
                    'size-1.5 rounded-full',
                    tab.exited
                      ? 'bg-foreground-subtle'
                      : 'animate-pulse bg-accent'
                  )}
                />
              </span>
            )}
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                tab.command !== null && 'font-mono'
              )}
            >
              {label}
            </span>
            <button
              type="button"
              aria-label={`Close ${label}`}
              className={cn(
                'flex size-4 shrink-0 items-center justify-center rounded text-foreground-subtle hover:bg-surface-hover hover:text-foreground',
                active ? 'opacity-80' : 'opacity-0 group-hover:opacity-80'
              )}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                sandboxTerminalPanelStore.requestKill(tab.key);
              }}
            >
              <Icons.cancel className="size-3" />
            </button>
          </div>
        );
      })}
      <IconButton
        size="sm"
        icon={Icons.plus}
        tooltip="New terminal"
        aria-label="New terminal"
        className="shrink-0"
        onClick={() => sandboxTerminalPanelStore.newTab()}
      />
    </XStack>
  );
});
